import { useEffect, useRef, useState } from 'react';

import { Alert, Button } from '@/components/ui';
import { useAnnouncer } from '@/lib/a11y';

import {
  COARSE_ACCURACY_M,
  GEOLOCATION_MESSAGES,
  GeolocationError,
  geolocationAvailable,
  locateDevice,
} from './geolocation';
import {
  DEFAULT_CENTER,
  DEFAULT_ZOOM,
  LOCATED_ZOOM,
  type LatLng,
  type MapPickerHandle,
  type MapProvider,
} from './mapProvider';
import { osmMapProvider } from './osmMapProvider';

export interface PickedLocation extends LatLng {
  /** How we got it. A rider should trust a fix and a pin differently. */
  source: 'device' | 'map';
  accuracyM: number | null;
}

export interface LocationPickerProps {
  value: PickedLocation | null;
  onChange: (location: PickedLocation) => void;
  /**
   * Roughly where to open, from the PIN code the customer just entered.
   *
   * Without it the map opens on the SHOP, which for anyone outside Kozhikode
   * is a map of somewhere they have never been and a long drag to anywhere
   * useful. A postal centroid is still a kilometre out, but it is the right
   * town — and by the time this renders the PIN code is known and checked, so
   * the information is already sitting there.
   */
  approxCentre?: LatLng | null;
  /** Swapped for Google later; the component never learns which is in use. */
  provider?: MapProvider;
}

/**
 * "Where is your door?" — asked once, answered two ways.
 *
 * A typed Indian address reliably gets a parcel to the street and no further.
 * "Near the temple, second lane" is a normal address line here, and the
 * difference between a pin and no pin is one delivery attempt versus a rider
 * phoning from the main road. That is why this is required rather than a nice
 * extra.
 *
 * TWO WAYS IN, because neither works alone. The device fix is one tap and
 * exact when it works; it is also the thing people decline, and it is useless
 * when they are ordering from the office for a delivery at home. The map
 * always works and is never wrong about what the customer meant, but it is
 * more effort. So: offer the fix, fall back to the map, and let the map
 * correct the fix.
 *
 * The map is the source of truth for the final value either way — a fix
 * recentres it, and whatever the customer leaves under the crosshair is what
 * gets saved.
 */
export function LocationPicker({
  value,
  onChange,
  approxCentre = null,
  provider = osmMapProvider,
}: LocationPickerProps) {
  const { announce } = useAnnouncer();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const handleRef = useRef<MapPickerHandle | null>(null);
  // The callback the map holds is captured once at creation, so it reads the
  // current onChange through a ref rather than pinning the first one.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const [ready, setReady] = useState(false);
  const [mapFailed, setMapFailed] = useState(false);
  const [locating, setLocating] = useState(false);
  const [locateError, setLocateError] = useState<string | null>(null);
  const [coarse, setCoarse] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const container = containerRef.current;
    if (!container) return;

    // A pin they already have beats the PIN code's centre, which beats the
    // shop. Only the first is an answer; the rest are somewhere to start.
    const start = value ?? approxCentre ?? DEFAULT_CENTER;
    provider
      .create(container, {
        center: start,
        zoom: value ? LOCATED_ZOOM : DEFAULT_ZOOM,
        label: 'Map. Move it to put the marker on your door.',
        onCenterChanged: (centre) => {
          // Moving the map IS the answer, so every settle is a new value.
          // Marked as 'map' because the customer has now overruled whatever
          // the device said, and a corrected fix is a pin.
          onChangeRef.current({ ...centre, source: 'map', accuracyM: null });
        },
      })
      .then((handle) => {
        if (cancelled) {
          handle.destroy();
          return;
        }
        handleRef.current = handle;
        setReady(true);
      })
      .catch(() => {
        if (!cancelled) setMapFailed(true);
      });

    return () => {
      cancelled = true;
      handleRef.current?.destroy();
      handleRef.current = null;
    };
    // Created once. `value` is deliberately not a dependency: re-creating the
    // map on every pan would destroy it mid-gesture.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider]);

  // The customer corrects their PIN code and the map follows — but only while
  // they have not set a pin themselves. Yanking the map out from under someone
  // who has just placed their marker would be worse than opening in the wrong
  // town in the first place.
  useEffect(() => {
    if (!ready || value !== null || !approxCentre) return;
    handleRef.current?.setCenter(approxCentre, DEFAULT_ZOOM);
    // `value` is intentionally read, not depended on: this must run when the
    // centre changes, and must not re-run when the customer moves the map.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, approxCentre?.lat, approxCentre?.lng]);

  async function locateMe() {
    setLocating(true);
    setLocateError(null);
    setCoarse(false);
    try {
      const located = await locateDevice();
      handleRef.current?.setCenter(located.coordinates, LOCATED_ZOOM);
      onChange({
        ...located.coordinates,
        source: 'device',
        accuracyM: located.accuracyM,
      });

      if (located.accuracyM !== null && located.accuracyM > COARSE_ACCURACY_M) {
        // Not a failure, but not a doorstep either. Say so rather than let
        // them believe a neighbourhood-level fix is their gate.
        setCoarse(true);
        announce('Location found, but only roughly. Please check the marker.');
      } else {
        announce('Location found. Check the marker is on your door.');
      }
    } catch (error) {
      const reason = error instanceof GeolocationError ? error.reason : 'unavailable';
      setLocateError(GEOLOCATION_MESSAGES[reason]);
      announce(GEOLOCATION_MESSAGES[reason], 'assertive');
    } finally {
      setLocating(false);
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <div>
        <p className="font-medium text-ink">Where is your door?</p>
        <p className="text-sm text-ink-muted">
          The rider uses this to find you. Use your location, then move the map if the marker is not
          quite right.
        </p>
      </div>

      {locateError ? (
        <Alert severity="warning" title="We could not use your location" live>
          <p>{locateError}</p>
        </Alert>
      ) : null}

      {coarse ? (
        <Alert severity="warning" title="That location is approximate" live>
          <p>
            Your device could only place you within about {value?.accuracyM ?? COARSE_ACCURACY_M}{' '}
            metres. Move the map so the marker sits on your building.
          </p>
        </Alert>
      ) : null}

      {mapFailed ? (
        <Alert severity="danger" title="The map could not load">
          <p>
            Check your connection and reload the page. You can still use your location if your
            browser allows it.
          </p>
        </Alert>
      ) : null}

      <div className="relative overflow-hidden rounded-card border border-line">
        <div ref={containerRef} className="h-64 w-full bg-surface-sunken" />

        {/* The crosshair. Fixed dead centre, on top of the map, and
            pointer-events-none so it never eats the pan gesture underneath. */}
        {ready ? (
          /* z-800 is not decoration. Leaflet's own panes climb to 700 — tiles
             200, markers 600, popups 700 — and they are children of the map
             container, so an overlay left at `z-index: auto` loses to every
             one of them and the pin is painted UNDER the map. Above the panes,
             below the controls at 1000, so +/- stay on top. */
          <div
            aria-hidden="true"
            data-testid="location-crosshair"
            className="pointer-events-none absolute inset-0 z-[800] grid place-items-center"
          >
            <svg viewBox="0 0 24 32" className="h-9 w-9 drop-shadow" fill="none">
              <path
                d="M12 31C12 31 21 19.5 21 12A9 9 0 1 0 3 12c0 7.5 9 19 9 19Z"
                className="fill-danger"
              />
              <circle cx="12" cy="12" r="3.25" className="fill-white" />
            </svg>
          </div>
        ) : (
          <div className="absolute inset-0 grid place-items-center text-sm text-ink-muted">
            Loading the map…
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="secondary"
          onClick={() => void locateMe()}
          loading={locating}
          loadingText="Finding you…"
          disabled={!geolocationAvailable()}
        >
          Use my location
        </Button>

        {value ? (
          <p className="text-sm text-ink-subtle">
            Marker set
            {value.source === 'device' && value.accuracyM !== null
              ? ` (±${value.accuracyM} m)`
              : ''}
          </p>
        ) : (
          <p className="text-sm text-ink-subtle">Move the map to set the marker.</p>
        )}
      </div>
    </div>
  );
}
